import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Plus, Search, Edit3, Trash2, Power, RotateCcw, Save, X, Loader2 } from "lucide-react";

const EMPTY = { category: "", label: "", text: "", order: 0, active: true };

export default function LiveSupportQuickReplies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [reseeding, setReseeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/live-support/quick-replies");
      setItems(data?.items || []);
    } catch (_) { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = Array.from(new Set(items.map((i) => i.category))).filter(Boolean).sort();
  const filtered = items.filter((i) => {
    if (cat !== "all" && i.category !== cat) return false;
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return (i.label || "").toLowerCase().includes(n) || (i.text || "").toLowerCase().includes(n) || (i.category || "").toLowerCase().includes(n);
  });

  const grouped = filtered.reduce((acc, i) => {
    (acc[i.category || "অন্যান্য"] = acc[i.category || "অন্যান্য"] || []).push(i);
    return acc;
  }, {});

  const startEdit = (item) => { setEditing(item); setDraft({ ...item }); };
  const startNew = () => { setEditing("new"); setDraft({ ...EMPTY, order: (Math.max(0, ...items.map((i) => i.order || 0)) + 10) }); };
  const cancelEdit = () => { setEditing(null); setDraft(EMPTY); };

  const save = async () => {
    if (!draft.category.trim() || !draft.label.trim() || !draft.text.trim()) {
      toast.error("Category, Label, and Text — সব ফিল্ড পূরণ করুন");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/live-support/quick-replies", draft);
        toast.success("নতুন quick reply যোগ হয়েছে");
      } else {
        await api.put(`/admin/live-support/quick-replies/${editing.id}`, draft);
        toast.success("Quick reply update হয়েছে");
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleActive = async (item) => {
    try {
      await api.put(`/admin/live-support/quick-replies/${item.id}`, { ...item, active: !item.active });
      load();
    } catch (_) { toast.error("Toggle failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Quick reply "${item.label}" delete করবেন?`)) return;
    try {
      await api.delete(`/admin/live-support/quick-replies/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (_) { toast.error("Delete failed"); }
  };

  const reseed = async () => {
    if (!window.confirm("সব Quick Reply রিসেট হবে এবং Maxlive88-এর built-in Bengali templates আবার লোড হবে। নিজের custom entries হারিয়ে যাবে। নিশ্চিত?")) return;
    setReseeding(true);
    try {
      const { data } = await api.post("/admin/live-support/quick-replies/reseed");
      toast.success(`Reseeded ${data?.count || 0} templates`);
      load();
    } catch (_) { toast.error("Reseed failed"); }
    finally { setReseeding(false); }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="💬 Live Chat Quick Replies"
        subtitle="Agent কোম্পোজারে দেখানো দ্রুত-উত্তর templates। Category, Label এবং Text edit করতে পারেন। Inactive করলে agent দেখবে না।"
      />

      {/* Toolbar */}
      <div className="bg-white border border-gray-200 rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="relative w-full sm:w-72">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search label / text / category…"
                 className="w-full h-9 pl-8 pr-3 rounded-sm border border-gray-200 text-sm focus:outline-none focus:border-emerald-500" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)}
                className="h-9 px-3 rounded-sm border border-gray-200 text-sm bg-white">
          <option value="all">All categories ({items.length})</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={reseed} disabled={reseeding} data-testid="ls-qr-reseed"
                  className="h-9 px-3 rounded-sm border border-amber-300 text-amber-700 bg-white hover:bg-amber-50 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
            {reseeding ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
            Reseed Defaults
          </button>
          <button onClick={startNew} data-testid="ls-qr-new"
                  className="h-9 px-3 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black inline-flex items-center gap-1.5">
            <Plus size={13} /> New Reply
          </button>
        </div>
      </div>

      {/* Editor (inline modal) */}
      {editing && (
        <div className="bg-white border-2 border-emerald-400 rounded-sm p-4 space-y-3" data-testid="ls-qr-editor">
          <div className="flex items-center justify-between">
            <div className="font-extrabold text-gray-900">{editing === "new" ? "✨ New Quick Reply" : `✏️ Edit · ${editing.label}`}</div>
            <button onClick={cancelEdit} className="h-8 w-8 rounded-sm hover:bg-gray-100 inline-flex items-center justify-center"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Category (যেমন: ডিপোজিট / এজেন্ট)">
              <input list="cat-list" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                     placeholder="ডিপোজিট"
                     className="w-full h-9 px-3 rounded-sm border border-gray-300 text-sm focus:outline-none focus:border-emerald-500" />
              <datalist id="cat-list">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            </Field>
            <Field label="Label (button-এ দেখাবে — short)">
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                     placeholder="Agent Deposit 5%"
                     className="w-full h-9 px-3 rounded-sm border border-gray-300 text-sm focus:outline-none focus:border-emerald-500" />
            </Field>
            <Field label="Order (numeric — low = first)">
              <input type="number" value={draft.order} onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value || "0", 10) })}
                     className="w-full h-9 px-3 rounded-sm border border-gray-300 text-sm focus:outline-none focus:border-emerald-500" />
            </Field>
          </div>
          <Field label="Reply Text (যা agent click করলে message-এ paste হবে)">
            <textarea rows={5} value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                      placeholder="💼 Agent এর মাধ্যমে Deposit করলে আপনি পাচ্ছেন 5% Bonus instantly! ..."
                      className="w-full px-3 py-2 rounded-sm border border-gray-300 text-sm focus:outline-none focus:border-emerald-500 resize-y font-mono leading-relaxed" />
          </Field>
          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} className="h-4 w-4" />
              <span className="font-semibold text-gray-700">Active (agent-কে দেখাবে)</span>
            </label>
            <div className="flex items-center gap-2">
              <button onClick={cancelEdit} className="h-9 px-4 rounded-sm border border-gray-200 hover:bg-gray-50 text-sm font-bold">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="ls-qr-save"
                      className="h-9 px-5 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-black inline-flex items-center gap-1.5 disabled:opacity-50">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {editing === "new" ? "Create" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grouped list */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-gray-500">
          <Loader2 size={14} className="animate-spin inline mr-2" />Loading…
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-gray-500">
          কোনো quick reply পাওয়া যায়নি। Reseed defaults বা New Reply ক্লিক করুন।
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).sort().map(([category, list]) => (
            <div key={category} className="bg-white border border-gray-200 rounded-sm overflow-hidden">
              <div className="px-4 py-2.5 bg-gradient-to-r from-emerald-50 to-transparent border-b border-gray-100 flex items-center justify-between">
                <div className="font-extrabold text-emerald-800 text-sm tracking-wide">{category}</div>
                <div className="text-[10px] text-gray-500 font-bold uppercase">{list.length} replies</div>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {list.sort((a, b) => (a.order || 0) - (b.order || 0)).map((r) => (
                    <tr key={r.id} className={`border-t border-gray-100 hover:bg-emerald-50/30 ${!r.active ? "opacity-50" : ""}`} data-testid={`ls-qr-row-${r.id}`}>
                      <td className="px-3 py-2.5 w-44 align-top">
                        <div className="font-bold text-gray-900 text-[13px]">{r.label}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">order {r.order}</div>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <div className="text-[12.5px] text-gray-700 whitespace-pre-wrap break-words leading-relaxed max-w-[600px]">{r.text}</div>
                      </td>
                      <td className="px-3 py-2.5 w-32 text-right align-top">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => toggleActive(r)} title={r.active ? "Deactivate" : "Activate"}
                                  className={`h-7 w-7 rounded-sm inline-flex items-center justify-center border ${r.active ? "border-emerald-300 text-emerald-600 hover:bg-emerald-50" : "border-gray-300 text-gray-400 hover:bg-gray-50"}`}>
                            <Power size={11} />
                          </button>
                          <button onClick={() => startEdit(r)} title="Edit" data-testid={`ls-qr-edit-${r.id}`}
                                  className="h-7 w-7 rounded-sm border border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 inline-flex items-center justify-center">
                            <Edit3 size={11} />
                          </button>
                          <button onClick={() => remove(r)} title="Delete"
                                  className="h-7 w-7 rounded-sm border border-gray-300 text-gray-700 hover:bg-red-50 hover:border-red-300 hover:text-red-700 inline-flex items-center justify-center">
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
